import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../client';

export interface AvailabilitySlot {
  id: number;
  therapistId: number;
  dayOfWeek: number;
  startTime: string;
  endTime: string;
  sessionType: 'video' | 'in_person' | 'both';
  bufferMinutes: number;
  createdAt: string;
  updatedAt: string;
}

export interface AvailabilityException {
  id: number;
  therapistId: number;
  date: string;
  allDay: boolean;
  startTime?: string;
  endTime?: string;
  reason?: string;
  createdAt: string;
  updatedAt: string;
}

export interface AvailableSlot {
  date: string;
  startTime: string;
  endTime: string;
  sessionType: string;
}

export interface CreateSlotRequest {
  dayOfWeek: number;
  startTime: string;
  endTime: string;
  sessionType: 'video' | 'in_person' | 'both';
  bufferMinutes?: number;
}

export interface UpdateSlotRequest extends CreateSlotRequest {
  id: number;
}

export interface CreateExceptionRequest {
  date: string;
  allDay?: boolean;
  startTime?: string;
  endTime?: string;
  reason?: string;
}

// Get therapist's availability slots
export function useAvailabilitySlots() {
  return useQuery({
    queryKey: ['availability-slots'],
    queryFn: async () => {
      const response = await apiClient.get<{ slots: AvailabilitySlot[] }>(
        '/therapist/availability'
      );
      return response.data.slots;
    },
  });
}

// Create new availability slot
export function useCreateSlot() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (slot: CreateSlotRequest) => {
      const response = await apiClient.post<AvailabilitySlot>(
        '/therapist/availability',
        slot
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['availability-slots'] });
    },
  });
}

// Update availability slot
export function useUpdateSlot() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...slot }: UpdateSlotRequest) => {
      const response = await apiClient.put<AvailabilitySlot>(
        `/therapist/availability/${id}`,
        slot
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['availability-slots'] });
    },
  });
}

// Delete availability slot
export function useDeleteSlot() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number) => {
      await apiClient.delete(`/therapist/availability/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['availability-slots'] });
    },
  });
}

// Get therapist's availability exceptions
export function useAvailabilityExceptions() {
  return useQuery({
    queryKey: ['availability-exceptions'],
    queryFn: async () => {
      const response = await apiClient.get<{ exceptions: AvailabilityException[] }>(
        '/therapist/availability/exceptions'
      );
      return response.data.exceptions;
    },
  });
}

// Create availability exception
export function useCreateException() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (exception: CreateExceptionRequest) => {
      const response = await apiClient.post<AvailabilityException>(
        '/therapist/availability/exceptions',
        exception
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['availability-exceptions'] });
    },
  });
}

// Delete availability exception
export function useDeleteException() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number) => {
      await apiClient.delete(`/therapist/availability/exceptions/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['availability-exceptions'] });
    },
  });
}

// Get available slots for public booking
export function usePublicAvailableSlots(
  therapistId: number,
  startDate: string,
  endDate: string
) {
  return useQuery({
    queryKey: ['public-available-slots', therapistId, startDate, endDate],
    queryFn: async () => {
      const response = await apiClient.get<{ slots: AvailableSlot[] }>(
        `/therapist/availability/available-slots/${therapistId}`,
        {
          params: { startDate, endDate },
        }
      );
      return response.data.slots;
    },
    enabled: !!therapistId && !!startDate && !!endDate,
  });
}
